import type { ComponentChildren } from "preact";
import { useEffect, useState } from "preact/hooks";
import { shortAddress } from "@tenergy/web-core/format";
import { adminEnv } from "./env.js";
import {
  SCREEN_GROUPS,
  SCREEN_HINTS,
  SCREEN_LABELS,
  routeHref,
  type AdminScreen,
  type Router,
} from "./router.js";
import { roleAtLeast, type AdminSessionRole } from "./api/types.js";
import { Toaster } from "./toast.js";
import { Icon, type IconName } from "./ui.js";

/**
 * The frame: a left sidebar of two-line items grouped by job (site-design.md §6, the TronSave
 * pattern), who is signed in, which right they hold, and the way out.
 *
 * THE ROLE IS ALWAYS ON SCREEN. An operator who cannot see a publish button should be able to
 * tell at a glance whether that is because they are a viewer or because the screen is broken,
 * and the pill at the foot of the sidebar is the answer.
 *
 * A link's accessible name is its title alone; the hint is its description
 * (`aria-describedby`), so "Supply" is still the link a screen reader and a test ask for.
 */

const ICONS: Record<AdminScreen, IconName> = {
  wallets: "wallet",
  monitoring: "pulse",
  providers: "stack",
  supply: "stack",
  "order-book": "book",
  prices: "tag",
  "promo-codes": "tag",
  "promo-app": "bolt",
  "promo-dev": "bolt",
  contracts: "doc",
  orders: "list",
  accounts: "user",
  "fund-report": "chart",
  "mcp-usage": "chart",
  finance: "chart",
  network: "bolt",
  pools: "stack",
  alerts: "bell",
  audit: "shield",
  subscriptions: "clock",
  "node-keys": "queue",
  withdrawals: "wallet",
  "own-supply": "bolt",
  "own-delegations": "list",
  "own-admission": "queue",
  "own-economics": "chart",
  "own-bandwidth": "pulse",
  "own-suppliers": "user",
  security: "shield",
  admins: "user",
  nile: "pulse",
};

const ROLE_TITLE: Record<AdminSessionRole, string> = {
  viewer: "Viewer: every admin read. Mutation controls are not rendered for this role.",
  editor: "Editor: every admin read and every admin mutation",
  admin: "Admin: an editor who also adds, changes and freezes viewers and editors",
  root: "Root: set in the API environment; manages every admin, ends any session",
};

export function Shell({
  address,
  role,
  router,
  onSignOut,
  children,
}: {
  address: string | null;
  role: AdminSessionRole;
  router: Router;
  onSignOut(): void;
  children: ComponentChildren;
}) {
  const env = adminEnv();
  // Only the current screen's group stays open; another one opens on a click, and a navigation closes it again.
  // The click is remembered WITH the screen it was made on, never reset by an effect: an effect still pending from
  // the shell's first render used to run after the click and close the group again.
  const currentGroup = SCREEN_GROUPS.find((group) => group.screens.includes(router.route.screen))?.label ?? null;
  const [choice, setChoice] = useState<{ screen: AdminScreen; group: string | null } | null>(null);
  const openGroup = choice !== null && choice.screen === router.route.screen ? choice.group : currentGroup;
  const setOpenGroup = (group: string | null) => setChoice({ screen: router.route.screen, group });
  // Below lg the sidebar is a drawer behind a "Menu" button (owner 2026-10-31: the whole menu stacked above every
  // page on a phone, its items scrolling sideways). A navigation closes it; so do Esc and a tap outside.
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => setMenuOpen(false), [router.route.screen, router.route.param]);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuOpen]);
  return (
    <div class="min-h-screen lg:grid lg:grid-cols-[248px_minmax(0,1fr)]">
      <header class="sticky top-0 z-30 flex items-center gap-2.5 border-b border-line bg-surface px-4 py-2.5 lg:hidden">
        <span class="flex size-7 shrink-0 items-center justify-center rounded-[9px] bg-accent text-accent-contrast">
          <Icon name="bolt" />
        </span>
        <span class="min-w-0 truncate text-[13.5px] font-semibold text-ink">{SCREEN_LABELS[router.route.screen]}</span>
        <span class="num ml-auto shrink-0 rounded-full border border-line px-2 py-0.5 text-[10px] tracking-wide text-ink-muted uppercase">
          {env.tronNetwork}
        </span>
        <button
          type="button"
          aria-expanded={menuOpen}
          aria-controls="admin-menu"
          onClick={() => setMenuOpen(true)}
          class="min-h-8 shrink-0 rounded-[10px] border border-line px-3 text-[12px] font-medium text-ink hover:border-accent/60"
        >
          Menu
        </button>
      </header>
      {menuOpen ? <div class="fixed inset-0 z-40 bg-black/60 lg:hidden" onClick={() => setMenuOpen(false)} /> : null}

      <aside
        id="admin-menu"
        class={`border-line bg-surface lg:static lg:block lg:w-auto lg:max-w-none lg:overflow-visible lg:border-r ${
          menuOpen ? "fixed inset-y-0 left-0 z-50 block w-[86vw] max-w-[320px] overflow-y-auto border-r" : "hidden"
        }`}
      >
        <div class="flex min-h-full flex-col lg:sticky lg:top-0 lg:h-screen lg:min-h-0">
        <div class="flex items-center gap-2.5 px-5 pt-5 pb-4">
          <span class="flex size-8 items-center justify-center rounded-[10px] bg-accent text-accent-contrast">
            <Icon name="bolt" />
          </span>
          <div class="min-w-0">
            <div class="font-display text-[15px] leading-none font-medium tracking-[-0.01em] text-ink">
              tenergy
            </div>
            <div class="num mt-1 text-[10px] tracking-[0.08em] text-ink-muted uppercase">
              platform admin
            </div>
          </div>
          <span
            class="num ml-auto rounded-full border border-line px-2 py-0.5 text-[10px] tracking-wide text-ink-muted uppercase"
            title={`TRON network this deployment serves: ${env.tronNetwork}`}
          >
            {env.tronNetwork}
          </span>
          <button
            type="button"
            aria-label="Close the menu"
            onClick={() => setMenuOpen(false)}
            class="flex size-8 items-center justify-center rounded-[10px] text-[18px] leading-none text-ink-muted hover:bg-surface-raised hover:text-ink lg:hidden"
          >
            ×
          </button>
        </div>

        <nav aria-label="Sections" class="flex-1 px-3 pb-3 lg:overflow-y-auto">
          {SCREEN_GROUPS.map((group) => {
            const open = !group.collapsible || openGroup === group.label;
            return (
              <div key={group.label} class="mb-3">
                {group.collapsible ? (
                  <button
                    type="button"
                    aria-expanded={open}
                    onClick={() => setOpenGroup(open ? null : group.label)}
                    class="num flex w-full items-center px-2 pb-1.5 text-left text-[10px] tracking-[0.1em] text-ink-muted/80 uppercase hover:text-ink"
                  >
                    {group.label}
                    <span class="ml-auto" aria-hidden="true">{open ? "−" : "+"}</span>
                  </button>
                ) : (
                  <div class="num px-2 pb-1.5 text-[10px] tracking-[0.1em] text-ink-muted/80 uppercase">{group.label}</div>
                )}
                {open ? (
                  <ul class="flex flex-col gap-1">
                    {group.screens.filter((screen) => screen !== "admins" || roleAtLeast(role, "admin")).map((screen) => (
                      <NavItem key={screen} screen={screen} router={router} />
                    ))}
                  </ul>
                ) : null}
              </div>
            );
          })}
        </nav>

        <div class="border-t border-line px-4 py-3">
          {/* The environment switch, not a section (owner 2026-10-22: «к низу прилепим рядом с местом где адрес и выход»). */}
          <ul class="mb-2 flex flex-col gap-1">
            <NavItem screen="nile" router={router} />
          </ul>
          <div class="flex items-center gap-2">
            {address === null ? null : (
              <span class="num min-w-0 truncate text-[11px] text-ink" title={address}>
                {shortAddress(address)}
              </span>
            )}
            <span
              class={`num ml-auto rounded-full px-2 py-0.5 text-[10px] ${
                roleAtLeast(role, "editor") ? "bg-accent-soft text-accent-text" : "bg-surface-raised text-ink-muted"
              }`}
              title={ROLE_TITLE[role]}
            >
              {role}
            </span>
          </div>
          <button
            type="button"
            onClick={onSignOut}
            class="mt-2 min-h-8 w-full rounded-[12px] border border-line text-[11.5px] text-ink-muted transition-colors hover:border-accent/60 hover:text-ink"
          >
            Sign out
          </button>
        </div>
        </div>
      </aside>

      <main id="main" class="min-w-0 px-4 py-5 lg:px-7 lg:py-6">
        <div class="mx-auto w-full max-w-[1320px]">{children}</div>
      </main>
      <Toaster />
    </div>
  );
}

/**
 * One two-line sidebar item. A link's accessible name is its title alone; the hint is its description.
 */
function NavItem({ screen, router }: { screen: AdminScreen; router: Router }) {
  const current = router.route.screen === screen;
  const hintId = `nav-hint-${screen}`;
  return (
    <li>
      <a
        href={routeHref(screen)}
        aria-label={SCREEN_LABELS[screen]}
        aria-describedby={hintId}
        aria-current={current ? "page" : undefined}
        onClick={(event) => router.onNavigate(event as MouseEvent, screen)}
        class={`flex items-start gap-2.5 rounded-[12px] border px-2.5 py-2 whitespace-nowrap transition-colors duration-150 ${
          current ? "border-accent/70 bg-accent-soft" : "border-transparent hover:bg-surface-raised"
        }`}
      >
        <span class={`mt-0.5 ${current ? "text-accent-text" : "text-ink-muted"}`}>
          <Icon name={ICONS[screen]} />
        </span>
        {/* A hint longer than the card ends in an ellipsis, the full text on hover (owner 2026-10-20). */}
        <span class="min-w-0 flex-1">
          <span class={`block truncate text-[12.5px] font-medium ${current ? "text-ink" : "text-ink/90"}`}>{SCREEN_LABELS[screen]}</span>
          <span id={hintId} title={SCREEN_HINTS[screen]} class="block truncate text-[10.5px] leading-snug text-ink-muted">
            {SCREEN_HINTS[screen]}
          </span>
        </span>
      </a>
    </li>
  );
}
